import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Stepper, Step, StepLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Chip,
  Grid, List, ListItem, ListItemIcon, ListItemText, Alert, LinearProgress,
  Paper, Divider, Tooltip, Badge
} from '@mui/material';
import {
  School, CheckCircle, PlayArrow, Pause, SkipNext, SkipPrevious,
  AutoAwesome, Psychology, TrendingUp, Business, SmartToy, DataUsage,
  Insights, Target, Schedule, Notifications, Settings, Rocket, Bolt,
  Zap, FlashOn, TrendingFlat, TrendingUpSharp, Star, StarBorder
} from '@mui/icons-material';

const OnboardingTutorial = ({ onComplete, onSkip }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showTutorial, setShowTutorial] = useState(true);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [currentFeature, setCurrentFeature] = useState(null);

  // Enhanced tutorial steps with advanced lead enrichment features
  const tutorialSteps = [
    {
      title: 'Welcome to Advanced Lead Enrichment',
      description: 'Discover the most powerful lead intelligence platform on the market',
      icon: <Rocket color="primary" />,
      content: {
        type: 'welcome',
        features: [
          'Real-time data enrichment from 100+ sources',
          'AI-powered behavioral analysis',
          'Intent detection and buying signals',
          'Predictive analytics and scoring',
          'Technology stack analysis',
          'Funding intelligence and growth signals'
        ]
      }
    },
    {
      title: 'Real-Time Enrichment Engine',
      description: 'Learn how our engine continuously monitors and enriches leads',
      icon: <Bolt color="primary" />,
      content: {
        type: 'feature',
        feature: 'realTimeEnrichment',
        demo: {
          title: 'Live Data Enrichment',
          description: 'Watch as we enrich lead data in real-time from multiple sources',
          steps: [
            'Scanning LinkedIn profiles for activity',
            'Analyzing Twitter engagement patterns',
            'Gathering company intelligence',
            'Detecting technology stack',
            'Calculating AI scores'
          ]
        }
      }
    },
    {
      title: 'AI-Powered Behavioral Analysis',
      description: 'Understand how AI analyzes online behavior and engagement patterns',
      icon: <Psychology color="primary" />,
      content: {
        type: 'feature',
        feature: 'behavioralAnalysis',
        demo: {
          title: 'Behavioral Intelligence',
          description: 'See how we analyze patterns to predict buying behavior',
          steps: [
            'Tracking social media activity',
            'Analyzing content engagement',
            'Identifying preferred platforms',
            'Detecting buying signals',
            'Predicting optimal contact times'
          ]
        }
      }
    },
    {
      title: 'Intent Detection & Buying Signals',
      description: 'Master the art of detecting real buying intent',
              icon: <TrendingUp color="primary" />,
      content: {
        type: 'feature',
        feature: 'intentDetection',
        demo: {
          title: 'Buying Intent Analysis',
          description: 'Learn to identify high-intent prospects automatically',
          steps: [
            'Monitoring search behavior',
            'Tracking website visits',
            'Analyzing content consumption',
            'Detecting engagement patterns',
            'Scoring buying probability'
          ]
        }
      }
    },
    {
      title: 'Advanced AI Scoring',
      description: 'Learn our multi-factor scoring system',
      icon: <SmartToy color="primary" />,
      content: {
        type: 'feature',
        feature: 'aiScoring',
        demo: {
          title: 'Intelligent Lead Scoring',
          description: 'See how we score leads using advanced AI algorithms',
          steps: [
            'Contact information scoring',
            'Social media influence analysis',
            'Company intelligence evaluation',
            'Technology stack assessment',
            'Behavioral pattern scoring',
            'Intent signal weighting'
          ]
        }
      }
    },
    {
      title: 'Technology Stack Analysis',
      description: 'Discover how we analyze company technology infrastructure',
      icon: <DataUsage color="primary" />,
      content: {
        type: 'feature',
        feature: 'technologyStack',
        demo: {
          title: 'Tech Stack Intelligence',
          description: 'Learn to identify integration opportunities',
          steps: [
            'Detecting current technologies',
            'Analyzing integration capabilities',
            'Identifying modernization needs',
            'Assessing security profiles',
            'Predicting technology trends'
          ]
        }
      }
    },
    {
      title: 'Predictive Analytics',
      description: 'Master predictive insights for better decision making',
      icon: <TrendingUp color="primary" />,
      content: {
        type: 'feature',
        feature: 'predictiveAnalytics',
        demo: {
          title: 'Predictive Intelligence',
          description: 'See how we predict conversion probability and deal size',
          steps: [
            'Conversion probability modeling',
            'Deal size estimation',
            'Lifetime value prediction',
            'Churn risk assessment',
            'Optimal contact timing'
          ]
        }
      }
    },
    {
      title: 'Advanced Settings & Configuration',
      description: 'Configure the enrichment engine to your needs',
      icon: <Settings color="primary" />,
      content: {
        type: 'feature',
        feature: 'settings',
        demo: {
          title: 'Engine Configuration',
          description: 'Learn to customize the enrichment process',
          steps: [
            'Data source selection',
            'AI model configuration',
            'Scoring weight adjustment',
            'Automation settings',
            'Integration preferences'
          ]
        }
      }
    },
    {
      title: 'Analytics & Reporting',
      description: 'Track enrichment performance and insights',
      icon: <Insights color="primary" />,
      content: {
        type: 'feature',
        feature: 'analytics',
        demo: {
          title: 'Performance Analytics',
          description: 'Monitor enrichment success and ROI',
          steps: [
            'Enrichment success rates',
            'Data source performance',
            'AI scoring accuracy',
            'Conversion tracking',
            'ROI measurement'
          ]
        }
      }
    },
    {
      title: 'Congratulations!',
      description: 'You\'re now ready to use the most advanced lead enrichment platform',
      icon: <CheckCircle color="primary" />,
      content: {
        type: 'completion',
        summary: [
          'Real-time enrichment from 100+ data sources',
          'AI-powered behavioral analysis and scoring',
          'Intent detection and buying signal identification',
          'Technology stack and company intelligence',
          'Predictive analytics and conversion modeling',
          'Advanced automation and customization'
        ]
      }
    }
  ];

  useEffect(() => {
    if (isPlaying) {
      const timer = setTimeout(() => {
        if (activeStep < tutorialSteps.length - 1) {
          setActiveStep(activeStep + 1);
        } else {
          setIsPlaying(false);
          handleComplete();
        }
      }, 5000); // 5 seconds per step

      return () => clearTimeout(timer);
    }
  }, [isPlaying, activeStep]);

  const handleNext = () => {
    if (activeStep < tutorialSteps.length - 1) {
      setActiveStep(activeStep + 1);
      setCompletedSteps([...completedSteps, activeStep]);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleComplete = () => {
    setShowTutorial(false);
    setCompletedSteps([...completedSteps, activeStep]);
    onComplete && onComplete();
  };

  const handleSkip = () => {
    setShowTutorial(false);
    onSkip && onSkip();
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const currentStep = tutorialSteps[activeStep];

  const renderWelcomeContent = () => (
    <Box>
      <Typography variant="h5" gutterBottom color="primary">
        🚀 Welcome to the Future of Lead Enrichment
      </Typography>
      <Typography variant="body1" paragraph>
        You now have access to the most advanced lead intelligence platform on the market. 
        Our AI-powered engine provides comprehensive insights that traditional tools simply can't match.
      </Typography>
      
      <Grid container spacing={2} sx={{ mt: 3 }}>
        {currentStep.content.features.map((feature, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <Card variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoAwesome color="primary" fontSize="small" />
                <Typography variant="body2">{feature}</Typography>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Pro Tip:</strong> This tutorial will take about 5 minutes. You can pause, skip, or replay any section.
        </Typography>
      </Alert>
    </Box>
  );

  const renderFeatureContent = () => (
    <Box>
      <Typography variant="h6" gutterBottom color="primary">
        {currentStep.content.demo.title}
      </Typography>
      <Typography variant="body1" paragraph>
        {currentStep.content.demo.description}
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, mt: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          How it works:
        </Typography>
        <List dense>
          {currentStep.content.demo.steps.map((step, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <Badge badgeContent={index + 1} color="primary">
                  <CheckCircle color="primary" fontSize="small" />
                </Badge>
              </ListItemIcon>
              <ListItemText primary={step} />
            </ListItem>
          ))}
        </List>
      </Paper>

      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Key Benefits:
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Chip icon={<TrendingUp />} label="Increased Accuracy" size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Chip icon={<Bolt />} label="Real-time Updates" size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Chip icon={<SmartToy />} label="AI-Powered Insights" size="small" />
          </Grid>
          <Grid item xs={12} sm={6}>
                            <Chip icon={<TrendingUp />} label="Better Targeting" size="small" />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );

  const renderCompletionContent = () => (
    <Box>
      <Typography variant="h5" gutterBottom color="primary" align="center">
        🎉 Congratulations!
      </Typography>
      <Typography variant="body1" paragraph align="center">
        You've completed the advanced lead enrichment tutorial. You're now ready to use the most powerful 
        lead intelligence platform on the market.
      </Typography>

      <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          What You've Learned:
        </Typography>
        <Grid container spacing={2}>
          {currentStep.content.summary.map((item, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Star color="primary" fontSize="small" />
                <Typography variant="body2">{item}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Alert severity="success" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Next Steps:</strong> Start enriching your leads and watch your conversion rates soar!
        </Typography>
      </Alert>
    </Box>
  );

  const renderContent = () => {
    switch (currentStep.content.type) {
      case 'welcome':
        return renderWelcomeContent();
      case 'feature':
        return renderFeatureContent();
      case 'completion':
        return renderCompletionContent();
      default:
        return null;
    }
  };

  if (!showTutorial) {
    return null;
  }

  return (
    <Dialog 
      open={showTutorial} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {currentStep.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Skip Tutorial">
              <IconButton onClick={handleSkip} size="small">
                <SkipNext />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {tutorialSteps.map((step, index) => (
              <Step key={index} completed={completedSteps.includes(index)}>
                <StepLabel>{step.title}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              {currentStep.icon}
              <Box>
                <Typography variant="h6">{currentStep.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {currentStep.description}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {renderContent()}

            {isPlaying && (
              <Box sx={{ mt: 3 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={((activeStep + 1) / tutorialSteps.length) * 100} 
                />
                <Typography variant="caption" color="text.secondary">
                  Auto-advancing in 5 seconds...
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            onClick={handleBack}
            disabled={activeStep === 0}
            startIcon={<SkipPrevious />}
          >
            Previous
          </Button>
          
          <Button
            onClick={togglePlay}
            variant="outlined"
            startIcon={isPlaying ? <Pause /> : <PlayArrow />}
          >
            {isPlaying ? 'Pause' : 'Auto-Play'}
          </Button>
          
          <Button
            onClick={handleNext}
            variant="contained"
            endIcon={activeStep === tutorialSteps.length - 1 ? <CheckCircle /> : <SkipNext />}
          >
            {activeStep === tutorialSteps.length - 1 ? 'Complete' : 'Next'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default OnboardingTutorial;
